/**
 * API base. On the web parent portal this is the relative '/api'. When this
 * module is reused inside the mobile (Capacitor) shell via the @parent alias —
 * e.g. the shared ChildSetupWizard now mounts natively — the WebView origin is
 * capacitor://localhost, so a relative '/api' never reaches the backend. There
 * we resolve the absolute API host exactly like web-child's resolveApiBase and
 * mobile/native.ts apiBase(). Resolved per-call so Capacitor is ready. On web
 * (no Capacitor) this is byte-identical to the old behaviour: '/api'.
 */
function apiBase(): string {
  try {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    if (cap?.isNativePlatform?.()) {
      const configured = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_API_BASE;
      return configured ? configured.replace(/\/$/, '') : 'https://aireadinglabs.com/api';
    }
  } catch { /* SSR / web / tests — fall through */ }
  return '/api';
}

/** True when this module runs inside the native (Capacitor) mobile shell. */
function isNativeShell(): boolean {
  try {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    return cap?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

/**
 * Native session-expired signal (2026-08-05 root-cause fix). On the WEB portal a
 * dead parent session redirects to /login — correct there. Inside the NATIVE
 * shell that same `window.location.href = '/login'` RELOADED the WebView to the
 * Start Learning page mid-flow: the tester's "hit Save and the app crashed back
 * to Start Learning" was this redirect, not a crash. On native we NEVER navigate
 * or silently clear tokens from this layer — the mobile shell owns its session.
 * We dispatch this event instead; the shell shows an explicit "please sign in
 * again" prompt and routes through its own auth flow.
 */
export const PARENT_SESSION_EXPIRED_EVENT = 'lwa:parent-session-expired';

function signalNativeSessionExpired(code?: string): void {
  try {
    window.dispatchEvent(new CustomEvent(PARENT_SESSION_EXPIRED_EVENT, { detail: { code: code ?? null } }));
  } catch { /* ignore */ }
}

import type {
  ChildProgramProfile, Program, SelectionSource, SchoolPlacement,
  EntryBand, RecommendationResult, ReadingCheckStatus,
} from '@learnwithai/ui-shared';
import { validateProgramProfile, parseSnapshot } from '@learnwithai/ui-shared';
import { getLessonsForLevel, getLessonById } from './offlineLessons';
import { getWritingLessonsForLevel, getWritingLessonById } from './offlineWritingLessons';
import { getOGLessonsForLevel, getOGLessonById } from './structuredLiteracyCurriculum';
import { getOGWritingLessonsForLevel, getOGWritingLessonById } from './structuredLiteracyWriting';
import { getSkillLevel } from './skillLevel';
import { getCustomLessonSummaries, getCustomLesson, getCustomWritingLessonSummaries, getCustomWritingLesson } from './customContentReader';

export interface PersonalitySignal {
  engagementScore: number;
  playfulnessDelta: number;
  focusDelta: number;
  challengeDelta: number;
  rewardDelta: number;
  stepByStepDelta: number;
}

export interface ParentControls {
  maxSessionMinutes: number;
  enableSpeechRecognition: boolean;
  enableTts: boolean;
  readToMeEnabled: boolean;
  readWithMeEnabled: boolean;
  lessonLevelLocks: number[];
  encouragementIntensity: string;
  // dailyGoalMinutes removed from the client contract (educator 0.11c): it was
  // stored server-side but had NO UI and NO consumer. The DB column is kept as a
  // harmless no-op (no migration); the client neither sends nor reads it.
  tutorSettingsLocked: boolean;
}

// Curriculum edition for the School Year Lessons / Learning Path experience.
// Deliberately stored separately from the UI locale (i18n.language). The
// authoritative read/write logic lives in web-child's curriculum/variant.ts;
// web-app only needs the type + the value to pass through child creation,
// session handoff and the dashboard summary.
export type CurriculumVariant = 'us' | 'ca' | 'uk' | 'fr';

/** One item-level lesson result record (Wave D school-usable records) — as
 *  served by account-api GET /children/{id}/lesson-results. Aggregates only. */
export interface LessonResultRecord {
  lessonId: string;
  subject: string;               // reading | writing | math | science | phonics
  kind: string;                  // lesson | summative | island
  grade: number | null;
  score: number;                 // 0-100
  decodingScore: number | null;  // 0-100, only when ASR actually measured
  attemptedAt: string;           // ISO
  durationSeconds: number;
  correctFirstTry: number;
  retried: number;
  revealed: number;
  attemptId: string;
  /** Server sign-and-verify (R4 finding 1): true only when this row's stored HMAC
   *  re-verifies against its fields — i.e. the score was server-stamped and untampered.
   *  Legacy/unsigned rows and any fail-closed (no prod key) rows read back false. */
  verified: boolean;
  /** When the server stamped this record (ISO); null on unverified rows. */
  serverStampedAt: string | null;
}

/** One wellbeing notice event (WS3.1 distressed-child parent notification) — as
 *  served by account-api GET /children/{id}/wellbeing. COPPA-minimal by design:
 *  category + time only, NEVER transcript content. */
export interface WellbeingEventRecord {
  id: string;
  category: string;      // today only 'distress' — render unknown values with a generic label
  occurredAt: string;    // ISO-8601 UTC
}

/** One completed placement-assessment run (assessment-api attempt history). */
export interface AssessmentAttemptRecord {
  completedAt: string | null;
  level: number | null;
  score: number | null;
}

// ── Billing status vocabulary (P1-9) ─────────────────────────────────────────
// The ONE frontend definition of subscription statuses. Mirrors billing-api's
// SubscriptionStatus enum (src/apps/billing-api/Program.cs) EXACTLY — the
// backend serializes the enum with .ToString(), so these strings are the wire
// contract. Never invent statuses here (the old 'PastDue'/'Canceled' checks
// matched nothing and silently disabled the warning banners).
export const SUBSCRIPTION_STATUSES = [
  'Free', 'Trial', 'Active', 'CancelAtPeriodEnd', 'GracePeriod', 'Expired', 'Refunded', 'Revoked',
] as const;
export type SubscriptionStatus = typeof SUBSCRIPTION_STATUSES[number];

/** billing-api GET /subscriptions/{parentId} */
export interface SubscriptionInfo {
  parentId?: string;
  status: SubscriptionStatus | string;
  entitlementActive: boolean;
  provider: string;
  planId: string | null;
  /** Prior plan preserved when a subscription lapses — drives the Resubscribe CTA (P1-5). */
  lastPlanId?: string | null;
  /** Server-decided trial eligibility — the client never chooses (P1-7). */
  trialEligible?: boolean;
  cancelAtPeriodEnd?: boolean;
  entitlementExpiresAt?: string | null;
  gracePeriodEndsAt?: string | null;
}

/** Subscription has ended — the parent needs a real resubscribe checkout CTA. */
export const isLapsedStatus = (status: string): boolean =>
  status === 'Expired' || status === 'Refunded' || status === 'Revoked';

/** Payment trouble but access retained — prompt to fix payment, don't scare. */
export const isGraceStatus = (status: string): boolean => status === 'GracePeriod';

/** Cancellation scheduled but still entitled — offer one-click reactivate. */
export const isCancelScheduled = (status: string, cancelAtPeriodEnd?: boolean): boolean =>
  status === 'CancelAtPeriodEnd' || !!cancelAtPeriodEnd;

/** assessment-api GET /children/{id}/progress (parent view, aggregates only). */
export interface ChildProgressResponse {
  currentLevel: number;
  totalSessions: number;
  totalLessons: number;
  averageScore: number;
  trend: string;
  onboardingAssessmentDone?: boolean;
  // Assessment results (tester item 4): the most recent two completed
  // placement runs + the kept-higher best, straight from assessment-api.
  bestAssessmentLevel?: number | null;
  bestAssessmentScore?: number | null;
  lastAssessment?: AssessmentAttemptRecord | null;
  previousAssessment?: AssessmentAttemptRecord | null;
}

// Editions a parent can pick. web-app can't import web-child's generated data
// index, so this list is maintained by hand and mirrors curriculum/data/index.json.
export const AVAILABLE_CURRICULUM_VARIANTS: CurriculumVariant[] = ['us', 'ca', 'uk', 'fr'];

// ── Program onboarding + Reading Check (lwa_program_choice_v1) ────────────────
// Server-side per-child program memory (account-api ChildProgramProfile) + the
// Foundations Reading Check session (assessment-api). All additive/nullable; the
// shared ChildProgramProfile shape + validator live in ui-shared/programOnboarding.

/** The fields a parent PUT to /children/{id}/program may set. Region + language
 *  are NOT here — they stay on ChildProfile (CurriculumVariant/Locale, the SSOT). */
export interface ProgramProfilePatch {
  activeProgram?: Program | null;
  selectionSource?: SelectionSource | null;
  overriddenRecommendation?: 'foundations' | 'grade-learning' | null;
  schoolPlacement?: SchoolPlacement | null;
  enrolledGrade?: number | null;
  gradeLearningStartGrade?: number | null;
}

/** Result of a program PUT. A stale baseVersion → { ok:false, conflict:true } with
 *  the fresh server state so the caller can surface a recoverable "refresh" UI
 *  (design decision 3 — never a silent last-write-wins on program selection). */
export type PutProgramResult =
  | { ok: true; profile: ChildProgramProfile }
  | { ok: false; conflict: true; profile: ChildProgramProfile | null };

/** The Reading Check session for a child (assessment-api), read for resume + results. */
export interface ReadingCheckSessionResponse {
  sessionId: string | null;
  status: ReadingCheckStatus;
  entryBand: EntryBand | null;
  sectionIndex: number;
  sectionsTotal: number;
  itemsAnswered: number;
  soundConfirmed: boolean;
  micAvailable: boolean;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
  /** Present only once the check has completed (server-recomputed aggregate). */
  result: RecommendationResult | null;
}

// The assessment-api serializes the reading-check session with FLAT aggregate
// fields (recommendedProgram, confidence, foundationsStage, frontierKey,
// strengthKeys[], focusKey, and domainOutcomes as a "domain:class,…" CSV STRING)
// — NOT the nested `result` object this app reads. A raw pass-through left
// `session.result` undefined, so a fully-COMPLETED check still rendered "No
// Reading Check yet" (and CI missed it because the tests mock the nested shape
// the server never actually produces). This adapter maps the wire shape into
// ReadingCheckSessionResponse. Web + native share this client, so it fixes both.
interface RawReadingCheckSession {
  sessionId?: string | null;
  childId?: string;
  status?: string;
  assessmentVersion?: string;
  formId?: string;
  entryBand?: EntryBand | null;
  sectionIndex?: number;
  sectionsTotal?: number;
  itemsAnswered?: number;
  soundConfirmed?: boolean;
  micAvailable?: boolean;
  startedAt?: string | null;
  updatedAt?: string | null;
  completedAt?: string | null;
  expiresAt?: string | null;
  recommendedProgram?: string | null;
  confidence?: string | null;
  foundationsStage?: string | null;
  frontierKey?: string | null;
  strengthKeys?: string[];
  focusKey?: string | null;
  domainOutcomes?: string | Record<string, string> | null;
  items?: unknown[];
}

/** Parse the server's "decoding:strong,fluency:developing" CSV → an object. */
export function parseDomainOutcomes(v: string | Record<string, string> | null | undefined): Record<string, string> {
  if (!v) return {};
  if (typeof v === 'object') return v;
  const out: Record<string, string> = {};
  for (const pair of v.split(',')) {
    const [d, c] = pair.split(':').map((s) => s.trim());
    if (d && c) out[d] = c;
  }
  return out;
}

/** Adapt the flat assessment-api session wire shape → ReadingCheckSessionResponse. */
export function adaptReadingCheckSession(raw: RawReadingCheckSession): ReadingCheckSessionResponse {
  const items = Array.isArray(raw.items) ? raw.items : [];
  // A result exists only once the server has recomputed the aggregate on /complete.
  const completed = raw.status === 'completed' && !!raw.recommendedProgram;
  return {
    sessionId: raw.sessionId ?? null,
    status: (raw.status as ReadingCheckStatus) ?? 'none',
    entryBand: raw.entryBand ?? null,
    sectionIndex: raw.sectionIndex ?? 0,
    sectionsTotal: raw.sectionsTotal ?? 0,
    itemsAnswered: raw.itemsAnswered ?? items.length,
    soundConfirmed: !!raw.soundConfirmed,
    micAvailable: !!raw.micAvailable,
    startedAt: raw.startedAt ?? null,
    completedAt: raw.completedAt ?? null,
    expiresAt: raw.expiresAt ?? null,
    result: completed
      ? {
          recommendation: raw.recommendedProgram as RecommendationResult['recommendation'],
          confidence: (raw.confidence ?? 'more-info') as RecommendationResult['confidence'],
          foundationsStage: (raw.foundationsStage ?? null) as RecommendationResult['foundationsStage'],
          frontierKey: raw.frontierKey ?? null,
          strengthKeys: (Array.isArray(raw.strengthKeys) ? raw.strengthKeys : []) as RecommendationResult['strengthKeys'],
          focusKey: (raw.focusKey ?? null) as RecommendationResult['focusKey'],
          domainOutcomes: parseDomainOutcomes(raw.domainOutcomes) as RecommendationResult['domainOutcomes'],
        }
      : null,
  };
}

async function programAuthFetch(path: string, init?: RequestInit, isRetry = false): Promise<Response> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${apiBase()}${path}`, {
    ...init,
    signal: init?.signal ?? AbortSignal.timeout(15000),
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  // The parent JWT only lives ~2h; refresh + retry once instead of letting the
  // whole program wizard degrade to "couldn't load" on a stale token.
  if (res.status === 401 && !isRetry && getRefreshToken()) {
    if (await refreshSession()) return programAuthFetch(path, init, true);
  }
  if (res.status === 401 && !isRetry && isNativeShell()) {
    // Native with no refresh token (or refresh dead): tell the shell so it can
    // prompt an explicit re-sign-in instead of the flow failing quietly.
    const body = await res.clone().json().catch(() => null);
    signalNativeSessionExpired(typeof body?.code === 'string' ? body.code : undefined);
  }
  return res;
}

interface Child {
  id: string;
  name: string;
  age: number;
  locale: string;
  startingLevel?: number;
  curriculumVariant?: CurriculumVariant;
}

export function getParentId(): string | null {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const padded = parts[1] + '='.repeat((4 - parts[1].length % 4) % 4);
    const payload = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
    return payload.sub ?? null;
  } catch { return null; }
}

const CHILDREN_KEY = 'learnwithai_children';

function getStoredChildren(): Child[] {
  try {
    return JSON.parse(localStorage.getItem(CHILDREN_KEY) || '[]');
  } catch { return []; }
}

function storeChildren(children: Child[]) {
  localStorage.setItem(CHILDREN_KEY, JSON.stringify(children));
}

export function isJwtLive(token: string | null): boolean {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const padded = parts[1] + '='.repeat((4 - parts[1].length % 4) % 4);
    const { exp } = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof exp === 'number' && exp * 1000 > Date.now();
  } catch { return false; }
}

async function request<T>(
  path: string,
  options?: RequestInit,
  requestOptions: { redirectOnUnauthorized?: boolean } = {},
  isRetry = false,
): Promise<T> {
  const redirectOnUnauthorized = requestOptions.redirectOnUnauthorized ?? true;
  const childToken = localStorage.getItem('child_auth_token');
  if (childToken && !isJwtLive(childToken)) {
    localStorage.removeItem('child_auth_token');
    localStorage.removeItem('learnwithai_active_child');
  }
  const token = localStorage.getItem('child_auth_token') || localStorage.getItem('auth_token');
  const res = await fetch(`${apiBase()}${path}`, {
    ...options,
    // A hung request (e.g. a stalled child-token mint) must reject, not await
    // forever — otherwise the caller's `finally` never runs and UI pins on a
    // "Starting…" spinner permanently. 15s ceiling; a caller-supplied signal wins.
    signal: options?.signal ?? AbortSignal.timeout(15000),
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  if (res.status === 401) {
    // Parent-token call with a stored refresh token: silently refresh + retry
    // once before treating the session as dead (the parent JWT only lives a few
    // hours — on mobile this is the NORMAL path after a device sleeps).
    const usedParentToken = !localStorage.getItem('child_auth_token');
    if (usedParentToken && !isRetry && getRefreshToken()) {
      const body = await res.clone().json().catch(() => null);
      if (body?.code !== 'signed-in-elsewhere' && await refreshSession()) {
        return request<T>(path, options, requestOptions, true);
      }
    }
    if (redirectOnUnauthorized) {
      // Single active sign-in: gateway 401s with code 'signed-in-elsewhere' when
      // this account signed in on another device. Flag it so the login page can
      // explain WHY the user was signed out instead of a silent bounce.
      await flagIfSignedInElsewhere(res);
      if (isNativeShell()) {
        // NATIVE: never location.href (it reloads the WebView to Start Learning
        // mid-flow — the "Save crashed the app" report) and never silently clear
        // the shell's tokens. Tell the shell; it re-auths explicitly.
        const body = await res.clone().json().catch(() => null);
        signalNativeSessionExpired(typeof body?.code === 'string' ? body.code : undefined);
      } else if (localStorage.getItem('child_auth_token')) {
        localStorage.removeItem('child_auth_token');
        localStorage.removeItem('learnwithai_active_child');
        // Trailing slash → child app served directly (200); '/learn' would
        // 301 through an insecure http:// redirect hop.
        window.location.href = '/learn/';
      } else if (!localStorage.getItem('learnwithai_admin_session')) {
        // Only redirect to /login for parent sessions — never kick an admin out
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
      }
    }
    throw new Error('Session expired. Please sign in again.');
  }
  if (!res.ok) {
    throw await toApiError(res);
  }
  return res.json();
}

/** Error carrying the HTTP status + machine-readable `code` from the API body. */
export interface ApiError extends Error { status?: number; code?: string }

async function toApiError(res: Response): Promise<ApiError> {
  const body = await res.json().catch(() => null);
  const msg = body?.message
    ?? body?.Message
    ?? (body?.errors ? Object.values(body.errors as Record<string, string[]>).flat().join('. ') : null)
    ?? body?.title
    ?? res.statusText;
  const err: ApiError = new Error(msg);
  err.status = res.status;
  if (typeof body?.code === 'string') err.code = body.code;
  return err;
}

/** Session-storage flag the login page reads to explain a signed-in-elsewhere sign-out. */
export const SIGNED_OUT_ELSEWHERE_KEY = 'lwa_signed_out_elsewhere';

async function flagIfSignedInElsewhere(res: Response): Promise<void> {
  try {
    const body = await res.clone().json().catch(() => null);
    if (body?.code === 'signed-in-elsewhere') {
      sessionStorage.setItem(SIGNED_OUT_ELSEWHERE_KEY, '1');
    }
  } catch { /* ignore */ }
}

// ── "Remember me" refresh token ───────────────────────────────────────────────
// Opaque, rotating refresh token issued by the backend when the user opts in.
// Stored client-side; exchanged for a fresh access JWT via /auth/token/refresh
// when the access token expires (silent refresh on 401 / app launch).
const REFRESH_TOKEN_KEY = 'parent_refresh_token';

export function storeRefreshToken(token?: string | null): void {
  if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token);
  else localStorage.removeItem(REFRESH_TOKEN_KEY);
}
function getRefreshToken(): string | null { return localStorage.getItem(REFRESH_TOKEN_KEY); }

// ── §MD.3 cross-tab single-refresher ─────────────────────────────────────────
// Refresh tokens ROTATE server-side, so two tabs presenting the same token means
// the loser trips reuse-detection (chain revoke → family logout storm). The
// server keeps a bounded grace window for that race (401 "refresh-superseded",
// nothing revoked), but the first line of defence is client-side: at most ONE
// tab per browser refreshes at a time, and a tab that waited on the lock re-reads
// the shared localStorage before spending its (possibly stale) token. Web Locks
// API when available; a timestamped localStorage lock (stale-broken after 10s so
// a crashed/suspended holder can never wedge refresh) for older WebViews.
const REFRESH_LOCK_NAME = 'lwa-parent-refresh';
const REFRESH_LOCK_KEY = 'lwa_parent_refresh_lock';
const REFRESH_LOCK_STALE_MS = 10_000;
const REFRESH_POLL_MS = 100;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Fallback cross-tab mutex on localStorage. Not perfectly atomic (localStorage
 *  has no CAS) — a residual race is absorbed by the server's grace window. */
async function withStorageLock<T>(fn: () => Promise<T>): Promise<T> {
  const me = `${Date.now()}:${Math.random().toString(36).slice(2)}`;
  const waitUntil = Date.now() + REFRESH_LOCK_STALE_MS;
  for (;;) {
    const held = localStorage.getItem(REFRESH_LOCK_KEY);
    if (held) {
      const ts = Number(held.split(':')[0]);
      const stale = !Number.isFinite(ts) || Date.now() - ts > REFRESH_LOCK_STALE_MS;
      if (!stale && Date.now() < waitUntil) { await sleep(REFRESH_POLL_MS); continue; }
      // Stale holder (crashed/suspended tab) or we've waited a full stale window
      // ourselves — break the lock rather than deadlock the refresh path.
    }
    localStorage.setItem(REFRESH_LOCK_KEY, me);
    break;
  }
  try {
    return await fn();
  } finally {
    if (localStorage.getItem(REFRESH_LOCK_KEY) === me) localStorage.removeItem(REFRESH_LOCK_KEY);
  }
}

async function withCrossTabRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  type LockManagerLike = { request<R>(name: string, cb: () => Promise<R> | R): Promise<R> };
  const locks = (navigator as unknown as { locks?: LockManagerLike }).locks;
  if (locks?.request) {
    let started = false;
    try {
      return await locks.request(REFRESH_LOCK_NAME, () => { started = true; return fn(); });
    } catch (err) {
      if (started) throw err; // fn itself failed — never run it twice
      return withStorageLock(fn); // lock machinery unavailable (odd WebView) — fall back
    }
  }
  return withStorageLock(fn);
}

/** Poll shared storage for the rotated refresh token another tab is writing. */
async function waitForRotatedRefreshToken(prev: string, timeoutMs = 2_000): Promise<string | null> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const cur = getRefreshToken();
    if (cur && cur !== prev) return cur;
    if (Date.now() >= deadline) return null;
    await sleep(REFRESH_POLL_MS);
  }
}

let refreshInFlight: Promise<boolean> | null = null;

/** Exchange the stored refresh token for a fresh access token. Coalesces
 *  concurrent same-tab callers into one request AND serializes across tabs via
 *  the cross-tab lock (§MD.3). Rotates the stored pair on success; clears it
 *  when the session is truly dead. Never redirects. */
export async function refreshSession(): Promise<boolean> {
  const rtAtCall = getRefreshToken();
  if (!rtAtCall) return false;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      return await withCrossTabRefreshLock(() => refreshWithLockHeld(rtAtCall));
    } catch {
      return false; // lock machinery failed — keep the token, try again next time
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

async function refreshWithLockHeld(rtAtCall: string): Promise<boolean> {
  // While we waited for the lock another tab may already have rotated: the shared
  // refresh token changed, and the winner wrote the fresh auth_token FIRST (see
  // write order below) — so recover from storage instead of spending our stale
  // copy, which would burn a second rotation (and, past the server's grace
  // window, read as token theft and revoke the whole chain).
  const rt = getRefreshToken();
  if (!rt) return false; // another tab found the session dead and cleared it
  if (rt !== rtAtCall) return true;
  try {
    const res = await fetch(`${apiBase()}/auth/token/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
      // Bounded UNDER the 10s storage-lock stale-break: a hung refresh must
      // release the lock (via catch/finally) before another tab breaks in and
      // races the same token anyway.
      signal: AbortSignal.timeout(8_000),
    });
    if (!res.ok) {
      // 401 "refresh-superseded" (§MD.3): the server saw this token was rotated
      // moments ago by ANOTHER refresher whose successor is still live — nothing
      // was revoked and no tokens were issued. The winner persists auth_token
      // then parent_refresh_token; wait briefly for those writes to land in
      // shared storage, then recover with them. Only if no rotated token ever
      // appears is this browser's session treated as dead (clear + sign-in).
      const body = await res.clone().json().catch(() => null);
      if (body?.code === 'refresh-superseded') {
        if (await waitForRotatedRefreshToken(rt)) return true;
      }
      storeRefreshToken(undefined);
      return false;
    }
    const data = await res.json().catch(() => null);
    if (data?.token) {
      // WRITE ORDER MATTERS: access token FIRST, then the rotated refresh token.
      // Other tabs treat a changed REFRESH_TOKEN_KEY as "tokens are fresh — retry
      // with the stored auth_token", so auth_token must already be the new one.
      localStorage.setItem('auth_token', data.token);
      storeRefreshToken(data.refreshToken); // rotated — old one is now dead
      return true;
    }
    storeRefreshToken(undefined);
    return false;
  } catch {
    return false; // network blip — keep the token, try again next time
  }
}

// Parent portal requests always use the parent JWT — never the child session token.
// A live child_auth_token from a parallel child session would pollute parent API calls
// (subscriptions, children list, settings) and cause 401s from the backend.
async function parentRequest<T>(
  path: string,
  options?: RequestInit,
  requestOptions: { redirectOnUnauthorized?: boolean } = {},
  isRetry = false,
): Promise<T> {
  const redirectOnUnauthorized = requestOptions.redirectOnUnauthorized ?? true;
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${apiBase()}${path}`, {
    ...options,
    // See request(): a stalled parent request (e.g. the child-token mint behind
    // "Start Learning") must time out so callers' finally-blocks always run.
    signal: options?.signal ?? AbortSignal.timeout(15000),
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  if (res.status === 401) {
    const body = await res.clone().json().catch(() => null);
    const signedInElsewhere = body?.code === 'signed-in-elsewhere';
    // "Remember me": an expired access token (e.g. after app launch) is silently
    // refreshed once and the request retried — but NOT when the account was taken
    // over on another device (signed-in-elsewhere is a deliberate sign-out).
    if (!isRetry && !signedInElsewhere && getRefreshToken()) {
      if (await refreshSession()) {
        return parentRequest<T>(path, options, requestOptions, true);
      }
    }
    if (redirectOnUnauthorized && !localStorage.getItem('learnwithai_admin_session')) {
      if (signedInElsewhere) sessionStorage.setItem(SIGNED_OUT_ELSEWHERE_KEY, '1');
      if (isNativeShell()) {
        // NATIVE: no location.href (it reloads the WebView to Start Learning
        // mid-flow) and no silent token clearing — the mobile shell owns the
        // session and shows an explicit sign-in-again prompt on this event.
        signalNativeSessionExpired(signedInElsewhere ? 'signed-in-elsewhere' : undefined);
        throw new Error('Session expired. Please sign in again.');
      }
      localStorage.removeItem('auth_token');
      storeRefreshToken(undefined);
      window.location.href = '/login';
      throw new Error('Session expired. Please sign in again.');
    }
    // No redirect (e.g. the login call itself): surface the real status/code.
    throw await toApiError(res);
  }
  if (!res.ok) {
    throw await toApiError(res);
  }
  return res.json();
}

// Admin-authed request. The admin dashboard authenticates with the ADMIN JWT
// (issued by identity-api /admin/login, stored under 'learnwithai_admin_jwt' by
// auth.ts) — NOT the parent `auth_token`. The admin invite/user endpoints gate on
// the JWT's staff/admin role claim, so these calls MUST carry the admin JWT;
// sending the parent token was why the Invites tab and admin user list 401'd.
async function adminRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const jwt = localStorage.getItem('learnwithai_admin_jwt');
  const res = await fetch(`${apiBase()}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...options?.headers,
    },
  });
  if (!res.ok) throw await toApiError(res);
  return res.json();
}

// ── Parent API ───────────────────────────────────────────────────────────────

export const parentApi = {
  // Auth
  // `force: true` takes over the account's single active session (evicts the
  // other device) after a 409 code:'session-conflict' response.
  login: (email: string, password: string, mfaCode?: string, force?: boolean, rememberMe?: boolean) =>
    parentRequest<{ token?: string; refreshToken?: string; mfaRequired?: boolean; mfaSetupRequired?: boolean; userId?: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, mfaCode, force, rememberMe }),
    }, { redirectOnUnauthorized: false }),

  // Best-effort server-side sign-out: releases the account's single-active-session
  // slot so the NEXT sign-in (this or any device) doesn't hit a spurious
  // "Another device is signed in" conflict. Never redirects (the caller is about
  // to clear tokens and navigate anyway) and callers should .catch(() => {}).
  logout: () => {
    // Drop the local "remember me" token immediately so a signed-out device can't
    // silently re-auth even if the network call to revoke it fails.
    storeRefreshToken(undefined);
    return parentRequest<{ message?: string }>('/auth/logout', { method: 'POST' }, { redirectOnUnauthorized: false });
  },

  // Open signup: `inviteCode` is OPTIONAL (school/tester codes remain a supported
  // path; the field is omitted entirely when the parent has none). During the
  // staged `Enrollment:InviteRequired` rollout the server may still reject a
  // code-less signup with code 'invite-required' — RegisterPage degrades gracefully.
  register: (email: string, password: string, locale: string, inviteCode?: string, rememberMe?: boolean, parentalConsent?: boolean) =>
    parentRequest<{ userId: string; token?: string; refreshToken?: string; mfaSetupRequired: boolean; message?: string }>('/auth/register', {
      method: 'POST',
      // COPPA/GDPR-K: /auth/register now REQUIRES parental consent and records the
      // legal artifact server-side. The RegisterPage already collects the checkbox;
      // send it (omit unless true).
      body: JSON.stringify({ email, password, locale, inviteCode: inviteCode || undefined, rememberMe, parentalConsent: parentalConsent === true ? true : undefined }),
    }),

  // Email verification (soft gate — a verified parent email is required before a
  // child session can start). Enumeration-safe on the backend.
  verifyEmail: (email: string, token: string) =>
    parentRequest<{ message: string }>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ email, token }),
    }, { redirectOnUnauthorized: false }),

  resendVerification: (email: string) =>
    parentRequest<{ message: string }>('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }, { redirectOnUnauthorized: false }),

  validateInvite: (code: string) =>
    parentRequest<{ valid: boolean; reason?: string }>('/auth/validate-invite', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }, { redirectOnUnauthorized: false }),

  // `name` is optional and forward-compatible: the backend send endpoint keys off
  // the email only (it does not yet persist a display name), so the name is passed
  // for the eventual personalization and echoed back in the UI confirmation.
  // Send an invite of a given enrollment track. `kind` defaults to 'tester' (FULL
  // access — the iOS TestFlight tester lane); 'trial' sends a limited Free-Preview
  // code. The response carries the iOS-only onboarding steps + the unique code —
  // NO web registration URL (the app is TestFlight-only).
  sendInvite: (email: string, name?: string, kind: 'tester' | 'trial' = 'tester') =>
    adminRequest<{ code: string; formattedCode: string; kind: 'tester' | 'trial'; platform: string; steps: string[]; message: string }>('/admin/invite/send', {
      method: 'POST',
      body: JSON.stringify({ email, name, kind }),
    }),

  // Mint N fresh invite codes (admin-only) of a given enrollment track. `kind`
  // defaults to 'tester' (FULL app access — the existing behavior); 'trial' mints
  // limited Free-Preview codes. Returns how many were added, the new total, + kind.
  generateInvites: (count: number, kind: 'tester' | 'trial' = 'tester') =>
    adminRequest<{ added: number; total: number; kind: 'tester' | 'trial' }>('/admin/invite/generate', {
      method: 'POST',
      body: JSON.stringify({ count, kind }),
    }),

  getInviteCodes: () =>
    adminRequest<Array<{
      id: string;
      code: string;
      available: boolean;
      usedAt: string | null;
      sentAt: string | null;
      usedByUserId: string | null;
      hasInvitedEmail: boolean;
      // Enrollment track this code grants. Legacy NULL rows surface as 'tester'
      // (full access), so the field is always present from the server; kept
      // optional here so older fixtures/callers stay valid (default ⇒ tester).
      kind?: 'tester' | 'trial';
      // Decrypted invited email (admin-only surface); null for un-sent codes.
      invitedEmail: string | null;
      // Derived invite lifecycle: 'available' | 'sent' | 'redeemed'.
      status: 'available' | 'sent' | 'redeemed';
      // Real SES delivery state from the email outbox: 'pending' | 'sent' | 'failed' | null.
      deliveryStatus: 'pending' | 'sent' | 'failed' | null;
    }>>('/admin/invite/codes'),

  // Real trial-enrollment users (EnrollmentKind=trial) — admin-only. Email is
  // decrypted server-side on this gated surface. Status is derived from the trial
  // window ('active' | 'expired'); 'converted' (paid) is a deferred billing join.
  getInviteTrialUsers: () =>
    adminRequest<Array<{
      id: string;
      email: string;
      code: string | null;
      formattedCode: string | null;
      registeredAt: string;
      lastLoginAt: string | null;
      trialStartedAt: string | null;
      trialEndsAt: string | null;
      status: 'active' | 'expired';
      daysLeft: number | null;
    }>>('/admin/invite/trial-users'),

  getAdminUsers: () =>
    adminRequest<Array<{ id: string; email: string; locale: string; role: string; createdAt: string; lastLoginAt: string | null; inviteCode: string | null; formattedInviteCode: string | null }>>('/admin/users'),

  deleteAdminUser: (userId: string) =>
    adminRequest<{ message: string }>(`/admin/users/${userId}`, { method: 'DELETE' }),

  createBug: (bug: { title: string; description: string; stepsToReproduce?: string; reporterEmail?: string; environment?: string; severity?: string }) =>
    parentRequest<{ issueNumber: number; issueUrl: string; message: string }>('/admin/bugs', {
      method: 'POST',
      body: JSON.stringify(bug),
    }),

  getBugs: () =>
    parentRequest<Array<{ number: number; title: string; state: string; url: string; createdAt: string; labels: string[] }>>('/admin/bugs'),

  confirmMfaSetup: (userId: string, code: string) =>
    parentRequest<{ message: string; recoveryCodes?: string[] }>('/auth/mfa/confirm-setup', {
      method: 'POST',
      body: JSON.stringify({ userId, code }),
    }),

  forgotPassword: (email: string) =>
    parentRequest<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (email: string, token: string, newPassword: string) =>
    parentRequest<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, token, newPassword }),
    }),

  getChildren: async (): Promise<Child[]> => {
    try {
      const parentId = getParentId();
      const remote = await parentRequest<Child[]>(
        parentId ? `/parents/${parentId}/children` : '/children',
        undefined,
        { redirectOnUnauthorized: false },
      );
      const remoteList = remote ?? [];
      // The backend may not (yet) echo curriculumVariant — it's a parent choice
      // stored locally. Preserve any locally-known variant so a dashboard reload
      // never drops it.
      const prev = getStoredChildren();
      const list = remoteList.map((c) => {
        if (c.curriculumVariant) return c;
        const local = prev.find((p) => p.id === c.id);
        return local?.curriculumVariant ? { ...c, curriculumVariant: local.curriculumVariant } : c;
      });
      storeChildren(list);
      return list;
    } catch {
      return getStoredChildren();
    }
  },

  createChild: async (name: string, age: number, locale: string, startingLevel?: number, curriculumVariant?: CurriculumVariant): Promise<{ id: string }> => {
    const res = await parentRequest<{ id: string }>('/children', {
      method: 'POST',
      body: JSON.stringify({ name, age, locale, startingLevel, curriculumVariant }),
    });
    const children = getStoredChildren();
    if (!children.some((c) => c.id === res.id)) {
      children.push({ id: res.id, name, age, locale, startingLevel: startingLevel || 1, curriculumVariant });
      storeChildren(children);
    }
    return { id: res.id };
  },

  updateChild: async (childId: string, updates: Partial<Omit<Child, 'id'>>): Promise<void> => {
    // account-api exposes PUT (not PATCH) for child updates. The server is the
    // canonical store, so let failures propagate — the caller must NOT report
    // success on a dropped write. Local cache is mirrored only after the server
    // confirms, so it never drifts ahead of the backend.
    await parentRequest(`/children/${childId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    const children = getStoredChildren();
    const idx = children.findIndex(c => c.id === childId);
    if (idx >= 0) {
      Object.assign(children[idx], updates);
      storeChildren(children);
    }
  },

  // Server-backed Learning Path summary: pull the child's synced progress
  // snapshot and extract the curriculum summary written by web-child/mobile.
  // Cross-device (unlike the same-origin localStorage cache). Null when absent.
  getChildCurriculumSummary: async (childId: string): Promise<Record<string, unknown> | null> => {
    try {
      const res = await parentRequest<{ snapshot?: string; Snapshot?: string }>(
        `/children/${childId}/progress`, undefined, { redirectOnUnauthorized: false },
      );
      // WS4: the snapshot is now the unified envelope — restore keys live under
      // `keys` (v2), NOT the top level. parseSnapshot reads that AND the legacy
      // top-level-key-map shape, so a summary written by either client is found.
      const parsed = parseSnapshot(res.snapshot ?? res.Snapshot);
      if (!parsed) return null;
      const summaryMap = parsed.keys['learnwithai_curriculum_summary'] as Record<string, Record<string, unknown>> | undefined;
      return summaryMap?.[childId] ?? null;
    } catch {
      return null;
    }
  },

  deleteChild: async (childId: string): Promise<void> => {
    const parentId = getParentId();
    if (parentId) {
      // Do not swallow errors — a network failure means the GDPR erasure did not
      // complete on the server. Let the caller surface this to the user.
      await parentRequest(`/parents/${parentId}/children/${childId}/privacy-data`, { method: 'DELETE' });
    }
    storeChildren(getStoredChildren().filter(c => c.id !== childId));
  },

  getChildProgress: (childId: string) =>
    parentRequest<ChildProgressResponse>(`/children/${childId}/progress`, undefined, { redirectOnUnauthorized: false }),

  // Parent "Redo Assessment": reset the child's onboarding so they re-take the placement test.
  resetChildAssessment: (childId: string) =>
    parentRequest<void>(`/assessments/onboarding-reset/${childId}`, { method: 'POST' }, { redirectOnUnauthorized: false }),

  // Wave D school-usable records: item-level lesson results for the Gradebook
  // tab + CSV export. Aggregates only — never the child's typed text or voice.
  getChildLessonResults: (childId: string, opts?: { subject?: string; kind?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.subject) params.set('subject', opts.subject);
    if (opts?.kind) params.set('kind', opts.kind);
    if (opts?.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return parentRequest<{ childId: string; results: LessonResultRecord[] }>(
      `/children/${childId}/lesson-results${qs ? `?${qs}` : ''}`, undefined, { redirectOnUnauthorized: false });
  },

  // WS3.1 wellbeing notices for the parent dashboard: category + time only
  // (COPPA-minimal — never transcript content). Newest first, last 90 days, max 20.
  getChildWellbeing: (childId: string) =>
    parentRequest<{ events: WellbeingEventRecord[] }>(
      `/children/${childId}/wellbeing`, undefined, { redirectOnUnauthorized: false }),

  getChildMastery: (childId: string) =>
    parentRequest<{
      childId: string;
      skills: Array<{ skill: string; mastery: number; band: string; attempts: number; correct: number; accuracy: number; hintsUsed: number }>;
      recommendedNextStep: string;
    }>(`/children/${childId}/mastery`, undefined, { redirectOnUnauthorized: false }),

  getParentControls: (parentId: string) =>
    parentRequest<ParentControls>(`/parents/${parentId}/settings`, undefined, { redirectOnUnauthorized: false }).catch(() => ({
      maxSessionMinutes: 30, enableSpeechRecognition: true, enableTts: true,
      readToMeEnabled: true, readWithMeEnabled: true, lessonLevelLocks: [],
      encouragementIntensity: 'balanced', tutorSettingsLocked: false,
    })),

  updateParentControls: (parentId: string, controls: Partial<ParentControls>) =>
    parentRequest<{ updated: boolean }>(`/parents/${parentId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(controls),
    }),

  captureConsent: (parentId: string, body: object) =>
    parentRequest<{ id: string; state: string }>(`/parents/${parentId}/consents`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  exportPrivacyData: (parentId: string) =>
    parentRequest<Record<string, unknown>>(`/parents/${parentId}/privacy/export`),

  optOutAnalytics: (parentId: string) =>
    parentRequest<{ analyticsOptOut: boolean }>(`/parents/${parentId}/privacy/analytics-opt-out`, {
      method: 'POST',
      body: '{}',
    }),

  getSubscription: (parentId: string) =>
    parentRequest<SubscriptionInfo>(`/subscriptions/${parentId}`, undefined, { redirectOnUnauthorized: false }),

  getEntitlement: (parentId: string) =>
    parentRequest<{ parentId: string; entitlementActive: boolean; provider: string; planId: string; expiresAt: string | null }>(`/subscriptions/${parentId}/entitlement`),

  // P1-7: NO trial parameter — the billing server alone decides trial eligibility
  // (one trial per parent). Anything the client sent would be ignored anyway.
  createCheckout: (
    parentId: string, priceId: string, successUrl: string, cancelUrl: string,
    email?: string, planId?: string, countryCode?: string,
    stripePriceId?: string, paddlePriceId?: string,
  ) =>
    parentRequest<{ sessionId: string | null; url: string | null; message?: string }>('/subscriptions/checkout', {
      method: 'POST',
      body: JSON.stringify({ parentId, priceId, successUrl, cancelUrl, email, planId, countryCode, stripePriceId, paddlePriceId }),
    }),

  getBillingPortal: (parentId: string, returnUrl: string) =>
    parentRequest<{ url: string | null; message?: string }>(`/subscriptions/portal?parentId=${encodeURIComponent(parentId)}&returnUrl=${encodeURIComponent(returnUrl)}`),

  createTeacherInvite: (childId: string, label?: string) =>
    parentRequest<{ id: string; token: string; label: string | null; childId: string; createdAt: string }>(`/children/${childId}/invites`, {
      method: 'POST',
      body: JSON.stringify({ label }),
    }),

  listTeacherInvites: (childId: string) =>
    parentRequest<Array<{ id: string; token: string; label: string | null; viewCount: number; lastViewedAt: string | null; createdAt: string }>>(`/children/${childId}/invites`),

  revokeTeacherInvite: (inviteId: string) =>
    parentRequest<{ revoked: boolean }>(`/invites/${inviteId}`, { method: 'DELETE' }),

  getTeacherProgress: (token: string) =>
    parentRequest<{ childAge: number; childLocale: string; label: string | null; totalSessions: number; totalLessons: number; averageScore: number; trend: string }>(`/invites/${token}/progress`),

  // Issues a short-lived child JWT and hands back to caller so DashboardPage
  // can store it and navigate to /learn in the same tab.
  childSession: (childId: string, childName: string) =>
    parentRequest<{ token: string }>('/auth/child-session', {
      method: 'POST',
      body: JSON.stringify({ childId, childName }),
    }),

  passkeyRegisterBegin: () =>
    parentRequest<Record<string, unknown>>('/auth/passkey/register/begin', { method: 'POST', body: '{}' }),

  passkeyRegisterComplete: (attestationResponse: unknown) =>
    parentRequest<{ message: string }>('/auth/passkey/register/complete', {
      method: 'POST',
      body: JSON.stringify(attestationResponse),
    }),

  passkeyLoginBegin: (email?: string) =>
    parentRequest<{ sessionId: string; options: Record<string, unknown> }>('/auth/passkey/login/begin', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  passkeyLoginComplete: (sessionId: string, assertionResponse: unknown) =>
    parentRequest<{ token: string }>('/auth/passkey/login/complete', {
      method: 'POST',
      body: JSON.stringify({ sessionId, assertionResponse }),
    }),

  subscribePush: (subscription: object) =>
    parentRequest<{ subscribed: boolean }>('/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    }),

  unsubscribePush: () =>
    parentRequest<{ unsubscribed: boolean }>('/push/unsubscribe', { method: 'DELETE' }),

  deleteAccount: () =>
    parentRequest<{ status: string }>('/auth/account', { method: 'DELETE' }),

  // ── Program onboarding + Reading Check ─────────────────────────────────────

  /** Read the child's server program profile. Absent / invalid → null so callers
   *  fall back to legacy (flag-OFF) behavior rather than trusting partial data. */
  getChildProgram: async (childId: string): Promise<ChildProgramProfile | null> => {
    try {
      const res = await programAuthFetch(`/children/${childId}/program`);
      if (!res.ok) return null;
      const body = await res.json().catch(() => null);
      return validateProgramProfile(body);
    } catch {
      return null;
    }
  },

  /** Write program-profile fields with optimistic concurrency. On a stale
   *  baseVersion the server 409s with the current state → returned as a
   *  recoverable conflict (never a silent overwrite). */
  putChildProgram: async (
    childId: string,
    patch: ProgramProfilePatch,
    baseVersion: number,
  ): Promise<PutProgramResult> => {
    const res = await programAuthFetch(`/children/${childId}/program`, {
      method: 'PUT',
      body: JSON.stringify({ ...patch, baseVersion }),
    });
    if (res.status === 409) {
      const body = await res.json().catch(() => null);
      return { ok: false, conflict: true, profile: validateProgramProfile(body) };
    }
    if (!res.ok) throw await toApiError(res);
    const body = await res.json().catch(() => null);
    const profile = validateProgramProfile(body);
    if (!profile) throw new Error('Program update returned an invalid profile.');
    return { ok: true, profile };
  },

  /** Create (or return the existing canonical) Reading Check session for a child.
   *  Idempotent server-side — a connection blip after S5 can't lose setup. */
  startReadingCheck: (childId: string, entryBand: EntryBand) =>
    parentRequest<RawReadingCheckSession>(
      `/assessments/reading-check/${childId}/start`,
      { method: 'POST', body: JSON.stringify({ entryBand }) },
      { redirectOnUnauthorized: false },
    ).then(adaptReadingCheckSession),

  /** The child's current Reading Check session (resume state + completed results). */
  getReadingCheckSession: (childId: string) =>
    parentRequest<RawReadingCheckSession>(
      `/assessments/reading-check/${childId}/session`,
      undefined,
      { redirectOnUnauthorized: false },
    ).then(adaptReadingCheckSession),

  /** Abandon the in-progress check (retake allowed immediately). */
  abandonReadingCheck: (childId: string) =>
    parentRequest<{ status: string }>(
      `/assessments/reading-check/${childId}/abandon`,
      { method: 'POST', body: '{}' },
      { redirectOnUnauthorized: false },
    ),

  /** DUAL-WRITE (design decision 2): mark the legacy onboarding gate satisfied with
   *  the parent-selected grade as StartingLevel. Writing BOTH the program profile
   *  (caller) AND this legacy record makes an un-gated Grade Learning child survive
   *  a full flag rollback (a rollback must never re-gate a real child into an
   *  assessment). Mirrors the child assessment completion path exactly
   *  (PUT /children/{id} StartingLevel + POST /assessments/onboarding-done/{id}). */
  markOnboardingDone: async (childId: string, startingLevel: number): Promise<void> => {
    await parentRequest(`/children/${childId}`, {
      method: 'PUT',
      body: JSON.stringify({ StartingLevel: startingLevel }),
    }, { redirectOnUnauthorized: false }).catch(() => { /* best effort */ });
    await parentRequest(`/assessments/onboarding-done/${childId}`, {
      method: 'POST',
      body: JSON.stringify({ startingLevel }),
    }, { redirectOnUnauthorized: false });
  },
};

// ── Child API ────────────────────────────────────────────────────────────────

export const api = {
  startSession: (childId: string, locale: string, adaptivePersonalityEnabled = true) =>
    request<{ sessionId: string }>('/sessions', {
      method: 'POST',
      body: JSON.stringify({ childId, locale, adaptivePersonalityEnabled }),
    }, { redirectOnUnauthorized: false }),

  sendMessage: (sessionId: string, message: string, adaptivePersonalityEnabled = true, signal?: PersonalitySignal) =>
    request<{ sessionId: string; response: string; filtered?: boolean }>(`/sessions/${sessionId}/message`, {
      method: 'POST',
      body: JSON.stringify({ message, adaptivePersonalityEnabled, signal }),
    }, { redirectOnUnauthorized: false }),

  updatePersonalitySettings: (childId: string, enabled: boolean) =>
    request<{ childId: string; isEnabled: boolean }>(`/sessions/children/${childId}/personality/settings`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  getParentControls: async () => {
    const parentId = getParentId();
    if (!parentId) return { tutorSettingsLocked: false } as ParentControls;
    return request<ParentControls>(`/parents/${parentId}/settings`).catch(() => ({
      maxSessionMinutes: 30, enableSpeechRecognition: true, enableTts: true,
      readToMeEnabled: true, readWithMeEnabled: true, lessonLevelLocks: [],
      encouragementIntensity: 'balanced', tutorSettingsLocked: false,
    }));
  },

  endSession: (sessionId: string) =>
    request<{ sessionId: string; status: string }>(`/sessions/${sessionId}/end`, { method: 'POST' }),

  getLessons: async (locale?: string, level?: number) => {
    const skillLevel = (level || getSkillLevel()) as 1 | 2 | 3 | 4 | 5;
    const isFr = locale?.startsWith('fr');
    const baseLessons = getLessonsForLevel(skillLevel);
    const ogLessons = getOGLessonsForLevel(skillLevel);
    const lessons = [...baseLessons, ...ogLessons].map((l) => ({
      id: l.id,
      title: isFr ? l.titleFr : l.title,
      locale: locale || 'en-US',
      difficulty: l.level,
    }));
    const custom = getCustomLessonSummaries();
    return [...custom, ...lessons];
  },

  getLesson: async (id: string, locale?: string) => {
    if (id.startsWith('custom-')) {
      const custom = getCustomLesson(id);
      if (custom) return custom;
    }
    const offlineFallback = () => {
      const lesson = getLessonById(id) ?? getOGLessonById(id);
      if (!lesson) throw new Error('Lesson not found');
      const isFr = locale?.startsWith('fr');
      return {
        id: lesson.id,
        title: isFr ? lesson.titleFr : lesson.title,
        contentBlocks: lesson.contentBlocks.map((b) => ({
          type: b.type,
          content: isFr && b.contentFr ? b.contentFr : b.content,
          audioRef: b.audioRef,
        })),
      };
    };
    try {
      const result = await request<{ id: string; title: string; contentBlocks: Array<{ type: string; content: string; audioRef?: string }> }>(
        `/lessons/${id}?locale=${encodeURIComponent(locale || 'en-US')}`,
        undefined,
        { redirectOnUnauthorized: false },
      );
      if (!result.contentBlocks || result.contentBlocks.length === 0) return offlineFallback();
      return result;
    } catch {
      return offlineFallback();
    }
  },

  startAssessment: (childId: string, locale: string) =>
    request<{ id: string; currentQuestion: number; totalQuestions: number }>('/assessments', {
      method: 'POST',
      body: JSON.stringify({ childId, locale }),
    }, { redirectOnUnauthorized: false }),

  submitAnswer: (assessmentId: string, questionNumber: number, answer: string) =>
    request<{ isCorrect: boolean; feedback: string; nextQuestion: number }>(`/assessments/${assessmentId}/answer`, {
      method: 'POST',
      body: JSON.stringify({ questionNumber, answer }),
    }, { redirectOnUnauthorized: false }),

  getResults: (assessmentId: string) =>
    request<{ score: number; total: number; areas: Array<{ area: string; score: number }> }>(`/assessments/${assessmentId}/results`),

  getProgress: (childId: string) =>
    request<{ totalSessions: number; totalLessons: number; averageScore: number; trend: string }>(`/children/${childId}/progress`),

  getAnimation: (key: string, locale?: string) =>
    request<string>(`/media/animations/${key}?locale=${locale || 'en-US'}`),

  getWritingLessons: async (locale?: string, level?: number) => {
    const skillLevel = (level || getSkillLevel()) as 1 | 2 | 3 | 4 | 5;
    const isFr = locale?.startsWith('fr');
    const baseWriting = getWritingLessonsForLevel(skillLevel);
    const ogWriting = getOGWritingLessonsForLevel(skillLevel);
    const lessons = [...baseWriting, ...ogWriting].map((l) => ({
      id: l.id,
      title: isFr ? l.titleFr : l.title,
      locale: locale || 'en-US',
      difficulty: l.level,
      level: l.level,
    }));
    const custom = getCustomWritingLessonSummaries().map((c) => ({
      id: c.id,
      title: isFr ? c.titleFr : c.title,
      locale: locale || 'en-US',
      difficulty: c.difficulty,
      level: c.level,
    }));
    return [...custom, ...lessons];
  },

  getWritingLesson: async (id: string, locale?: string) => {
    if (id.startsWith('custom-w-')) {
      const custom = getCustomWritingLesson(id);
      if (custom) {
        const isFr = locale?.startsWith('fr');
        return {
          id: custom.id,
          title: isFr ? custom.titleFr : custom.title,
          contentBlocks: custom.contentBlocks.map((b) => ({
            ...b,
            content: isFr && b.contentFr ? b.contentFr : b.content,
            answer: isFr && b.answerFr ? b.answerFr : b.answer,
            options: isFr && b.optionsFr ? b.optionsFr : b.options,
          })),
        };
      }
    }
    const lesson = getWritingLessonById(id) ?? getOGWritingLessonById(id);
    if (!lesson) throw new Error('Writing lesson not found');
    const isFr = locale?.startsWith('fr');
    return {
      id: lesson.id,
      title: isFr ? lesson.titleFr : lesson.title,
      contentBlocks: lesson.contentBlocks.map((b) => ({
        ...b,
        content: isFr && b.contentFr ? b.contentFr : b.content,
        answer: isFr && b.answerFr ? b.answerFr : b.answer,
        options: isFr && b.optionsFr ? b.optionsFr : b.options,
      })),
    };
  },
};
